import React from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { CButton } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload } from '@coreui/icons'
import FailureCard from './FailureCard'
import routePaths from '../../routes/route-paths'

interface BoundaryProps {
  children: ReactNode
  pathname: string
  onLeave: () => void
  t: TFunction<'common'>
}

interface BoundaryState {
  error: Error | null
  reference: string | null
}

class Boundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, reference: null }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error, reference: crypto.randomUUID() }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Read from state rather than minted here, so the logged reference is the one on the card.
    console.error('Unhandled render error', this.state.reference, error, info.componentStack)
  }

  componentDidUpdate(prevProps: BoundaryProps) {
    if (this.state.error && prevProps.pathname !== this.props.pathname) {
      this.setState({ error: null, reference: null })
    }
  }

  render() {
    const { error, reference } = this.state
    const { t } = this.props
    if (!error) {
      return this.props.children
    }

    return (
      <FailureCard
        title={t('errorBoundary.title')}
        message={t('errorBoundary.message')}
        reference={reference}
        details={error.message || null}
      >
        <div className="d-flex flex-column gap-2">
          <CButton color="primary" onClick={() => this.setState({ error: null, reference: null })}>
            <CIcon icon={cilReload} className="me-2" />
            {t('errorBoundary.tryAgain')}
          </CButton>
          <CButton
            color="link"
            onClick={() => {
              this.setState({ error: null, reference: null })
              this.props.onLeave()
            }}
          >
            {t('errorBoundary.goToDashboard')}
          </CButton>
        </div>
      </FailureCard>
    )
  }
}

/**
 * Catches a render failure anywhere below it and shows the failure card instead of the blank page
 * React leaves when it unmounts the tree.
 *
 * Clears itself when the path changes, so staff are not left on the error card after navigating
 * away. Not keyed on the path as the front office's is: a key rebuilds everything below it on every
 * navigation, and every route here is lazy, so each first visit to a page would drop to the
 * full-page spinner instead of holding the current page while the next one loads. It sits outside
 * RequireAuthentication so a failure during sign-in is caught too.
 */
const AppErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  return (
    <Boundary pathname={pathname} t={t} onLeave={() => navigate(routePaths.Dashboard)}>
      {children}
    </Boundary>
  )
}

export default AppErrorBoundary
